import { ChevronDown } from "lucide-react";
import { formatDate, formatFullDate } from "../../lib/utils";
import type { StoryWithUIState } from "../../types";
import { StoryThumbnail } from "../StoryThumbnail";
import { SharedBadge } from "./SharedBadge";
import { StoryActionButtons } from "./StoryActionButtons";
import { StoryProgressBar } from "./StoryProgressBar";
import { StoryStatusBadge } from "./StoryStatusBadge";

interface StoryTableRowProps {
    story: StoryWithUIState;
    onStoryClick: (storyId: string) => void;
    onDelete: (story: { id: string; title?: string; topic: string }) => void;
}

export function StoryTableRow({ story, onStoryClick, onDelete }: StoryTableRowProps) {
    return (
        <tr
            className="border-b cursor-pointer transition-colors duration-0 hover:[background-color:hsl(var(--primary)/0.08)]"
            onClick={() => onStoryClick(story.id)}
        >
            <td className="p-4">
                <div className="flex items-center gap-3">
                    <StoryThumbnail storyId={story.id} size="md" />
                    {story.hasShare && <SharedBadge />}
                </div>
            </td>
            <td className="p-4 font-medium">
                <div className="space-y-1">
                    <div className="text-foreground">{story.title || story.topic}</div>
                    {story.title && <div className="text-xs text-muted-foreground">Topic: {story.topic}</div>}
                </div>
            </td>
            <td className="p-4 select-none">
                <StoryProgressBar
                    currentStep={story.current_step}
                    totalSteps={story.total_steps}
                    status={story.status}
                />
            </td>
            <td className="p-4 select-none">
                <StoryStatusBadge status={story.status} />
            </td>
            <td className="p-4 text-sm text-muted-foreground select-none">
                <span title={formatFullDate(story.updated_at)}>{formatDate(story.updated_at)}</span>
            </td>
            <td className="p-4" onClick={(e) => e.stopPropagation()}>
                <StoryActionButtons
                    status={story.status}
                    onView={() => onStoryClick(story.id)}
                    onDelete={() => onDelete({ id: story.id, title: story.title, topic: story.topic })}
                />
            </td>
        </tr>
    );
}

interface StoryTableHeaderProps {
    sortBy: "updated_at" | "topic";
    sortOrder: "asc" | "desc";
    onSortChange: (sortBy: "updated_at" | "topic") => void;
}

export function StoryTableHeader({ sortBy, sortOrder, onSortChange }: StoryTableHeaderProps) {
    return (
        <thead className="border-b">
            <tr>
                <th className="text-left p-4">Thumbnail</th>
                <th
                    className={`text-left p-4 cursor-pointer hover:bg-primary/10 select-none transition-colors ${
                        sortBy === "topic" ? "bg-primary/10" : ""
                    }`}
                    onClick={() => onSortChange("topic")}
                >
                    <div className="flex items-center gap-1">
                        Title/Topic
                        <ChevronDown
                            className={`h-5 w-5 transition-transform ${
                                sortBy === "topic"
                                    ? sortOrder === "desc"
                                        ? "rotate-180 text-primary"
                                        : "text-primary"
                                    : "opacity-40 text-muted-foreground scale-75"
                            }`}
                        />
                    </div>
                </th>
                <th className="text-left p-4">Progress</th>
                <th className="text-left p-4">Status</th>
                <th
                    className={`text-left p-4 cursor-pointer hover:bg-primary/10 select-none transition-colors ${
                        sortBy === "updated_at" ? "bg-primary/10" : ""
                    }`}
                    onClick={() => onSortChange("updated_at")}
                >
                    <div className="flex items-center gap-1">
                        Last Updated
                        <ChevronDown
                            className={`h-5 w-5 transition-transform ${
                                sortBy === "updated_at"
                                    ? sortOrder === "desc"
                                        ? "rotate-180 text-primary"
                                        : "text-primary"
                                    : "opacity-40 text-muted-foreground scale-75"
                            }`}
                        />
                    </div>
                </th>
                <th className="text-right p-4">Action</th>
            </tr>
        </thead>
    );
}
